import { useSyncExternalStore } from 'react'

/**
 * Extractor authoring drafts — localStorage v1 (W5.2, docs/extractors-requirements.md §W5).
 *
 * A guided create that has not been created yet lives HERE, in this browser:
 * the backend cannot park an unfinished extractor (a `_draft` id cannot be read
 * back, CB-29) and W5 takes no backend asks. Live and demo behave the same —
 * the store IS the persistence for pre-create work either way.
 *
 * THIS SHAPE IS A DRAFT API CONTRACT (docs/extractors.md §Authoring store):
 * if a server-side draft ever becomes readable, `AuthoringDraft` is the body
 * the console would send. Storage: ONE key per cluster+scope,
 * `bloo.extractor-authoring.<clusterId>.<scopeId>`, holding
 * `{ [draftId]: AuthoringDraft }`. House rules (cf. extractor-store):
 * try/catch everywhere, SSR-safe, a corrupt blob is superseded by the next
 * write, never a crash.
 */

export interface AuthoringDraft {
  /** `local-…`, minted client-side; never a backend id. */
  id: string
  /** The row name the create will send. */
  name: string
  /** The YAML text as the author last left it (the truth on the create page). */
  yaml: string
  samples: string[]
  createdAt: number
  updatedAt: number
}

export type AuthoringMap = Record<string, AuthoringDraft>

export const AUTHORING_KEY_PREFIX = 'bloo.extractor-authoring.'

export function authoringStorageKey(clusterId: string, scopeId: string): string {
  return `${AUTHORING_KEY_PREFIX}${clusterId}.${scopeId}`
}

export function isLocalDraftId(id: string): boolean {
  return id.startsWith('local-')
}

export function newLocalDraftId(): string {
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

export interface AuthoringStore {
  /** Stable snapshot until the next mutation. */
  all: () => AuthoringMap
  get: (id: string) => AuthoringDraft | null
  /** Upsert; `createdAt` is kept from the existing entry, `updatedAt` stamped now. */
  save: (draft: Omit<AuthoringDraft, 'createdAt' | 'updatedAt'>) => AuthoringDraft
  remove: (id: string) => void
  reset: () => void
  subscribe: (cb: () => void) => () => void
}

const EMPTY: AuthoringMap = Object.freeze({}) as AuthoringMap

class LocalAuthoringStore implements AuthoringStore {
  private cache: AuthoringMap | null = null
  private listeners = new Set<() => void>()
  constructor(private readonly key: string) {}

  all = (): AuthoringMap => this.read()

  get = (id: string): AuthoringDraft | null => this.read()[id] ?? null

  save = (draft: Omit<AuthoringDraft, 'createdAt' | 'updatedAt'>): AuthoringDraft => {
    const cur = this.read()
    const now = Date.now()
    const next: AuthoringDraft = { ...draft, samples: [...draft.samples], createdAt: cur[draft.id]?.createdAt ?? now, updatedAt: now }
    this.write({ ...cur, [draft.id]: next })
    return next
  }

  remove = (id: string) => {
    const cur = this.read()
    if (!(id in cur)) return
    const { [id]: _dropped, ...rest } = cur
    this.write(rest)
  }

  reset = () => {
    try {
      globalThis.localStorage?.removeItem(this.key)
    } catch {
      /* no storage */
    }
    this.cache = EMPTY
    this.notify()
  }

  subscribe = (cb: () => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  private read(): AuthoringMap {
    if (this.cache) return this.cache
    let parsed: unknown
    try {
      const raw = globalThis.localStorage?.getItem(this.key)
      parsed = raw ? JSON.parse(raw) : {}
    } catch {
      parsed = {}
    }
    const map: AuthoringMap = {}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (!v || typeof v !== 'object') continue
        const d = v as Record<string, unknown>
        if (typeof d.yaml !== 'string') continue
        map[id] = {
          id,
          name: typeof d.name === 'string' ? d.name : '',
          yaml: d.yaml,
          samples: Array.isArray(d.samples) ? d.samples.filter((x): x is string => typeof x === 'string') : [],
          createdAt: typeof d.createdAt === 'number' ? d.createdAt : 0,
          updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : 0,
        }
      }
    }
    this.cache = map
    return map
  }

  private write(next: AuthoringMap) {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(next))
    } catch {
      /* quota or no storage: keep the in-memory map so the page still reflects the edit */
    }
    this.cache = next
    this.notify()
  }

  private notify() {
    for (const cb of this.listeners) cb()
  }
}

const stores = new Map<string, AuthoringStore>()

/** One store per cluster+scope, shared by the create page and the catalogue. */
export function authoringStore(clusterId: string, scopeId: string): AuthoringStore {
  const key = authoringStorageKey(clusterId, scopeId)
  let s = stores.get(key)
  if (!s) {
    s = new LocalAuthoringStore(key)
    stores.set(key, s)
  }
  return s
}

/** Live list of local drafts for one scope, newest first. */
export function useAuthoringDrafts(clusterId: string, scopeId: string): AuthoringDraft[] {
  const store = authoringStore(clusterId, scopeId)
  const map = useSyncExternalStore(store.subscribe, store.all, store.all)
  return Object.values(map).sort((a, b) => b.updatedAt - a.updatedAt)
}

/** Demo seeder body: forget every local draft in this browser. Idempotent. */
export function resetAllAuthoringDrafts(): void {
  const ls = globalThis.localStorage
  if (!ls) return
  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const key = ls.key(i)
      if (key && key.startsWith(AUTHORING_KEY_PREFIX)) ls.removeItem(key)
    }
  } catch {
    /* no storage */
  }
  for (const s of stores.values()) s.reset()
}
